/**
 * Schema Exports
 *
 * Zod schemas for runtime validation.
 */

// Common schemas
export {
  SessionIdSchema,
  AgentIdSchema,
  ToolUseIdSchema,
  SdkSessionIdSchema,
  PermissionModeSchema,
  // Permissions v2 — access level dial
  AccessLevelSchema,
  AccessLevelSettingsSchema,
  type AccessLevel,
  type AccessLevelSettings,
  SessionStatusSchema,
  ImageMediaTypeSchema,
  ImageAttachmentSchema,
  FileAttachmentSchema,
  QuestionOptionSchema,
  QuestionSchema,
  TodoStatusSchema,
  TodoSchema,
  SerializedSessionSchema,
  // K6 P3 C0 — permission schemas
  PermissionBehaviorSchema,
  PermissionRuleValueSchema,
  PermissionUpdateDestinationSchema,
  PermissionUpdateSchema,
} from './common.schema.js';

// Permissions v2 B1b — custom access config (zod write-path mirror of the
// agent-sdk AccessConfig type)
export { AccessConfigSchema, type AccessConfig } from './access-config.schema.js';

// Event payload schemas
export {
  // Session events
  SessionCreatePayloadSchema,
  SessionCreatedPayloadSchema,
  SessionListPayloadSchema,
  SessionResumePayloadSchema,
  SessionUpdatePayloadSchema,
  SessionDestroyPayloadSchema,
  SessionConfigPayloadSchema,

  // Agent registration
  AgentRegisterPayloadSchema,

  // User messages
  UserMessagePayloadSchema,
  UserMessageToAgentPayloadSchema,

  // Agent lifecycle
  AgentStartPayloadSchema,
  AgentEndPayloadSchema,
  AgentErrorPayloadSchema,
  AgentInterruptPayloadSchema,

  // SDK system
  SystemInitPayloadSchema,
  AgentResultPayloadSchema,

  // Content blocks
  AgentThinkingPayloadSchema,
  AgentThinkingDeltaPayloadSchema,
  AgentThinkingStartPayloadSchema,
  AgentTextPayloadSchema,
  AgentTextDeltaPayloadSchema,
  AgentToolUsePayloadSchema,
  AgentToolResultPayloadSchema,

  // Agent todo
  // Phase 9: PendingQuestion / QuestionAnswer / PendingApproval / ApprovalResponse
  // / PermissionRequest / PermissionResponse schemas removed — superseded by
  // the unified InteractionRequestSchema / InteractionOutcomeSchema.
  AgentTodoPayloadSchema,

  // Phase 6 branch

  // Embedded browser chrome navigation (browser:navigate) — the sandbox
  // handler validates inbound payloads with this exact schema.
  BrowserNavigatePayloadSchema,

  // Pane visibility (browser:pane_visibility) — the web's hidden/visible edge.
  BrowserPaneVisibilityPayloadSchema,

  // Embedded browser chrome tab strip (browser:tabs) — the runner's snapshot
  // emission for the pane's tab strip.
  BrowserTabsPayloadSchema,

  // A3-T2 Reach v1 (nami:notify) — the out-of-band frame the web parses before
  // it renders anything, so the schema has to cross the package boundary.
  NamiNotifyPayloadSchema,
  // composer:screen (spec 2026-09-18, R-L1): the web types the frame it emits against it.
  ComposerScreenPayloadSchema,
  // Engines T0: the person's line in a task, and the gate refusal it is composed from. The web
  // parses the first before it renders anything; the server composes from the second.
  TaskNoticePayloadSchema,
  PermissionDeniedPayloadSchema,
  // Engines T2a: the web parses a connect's progress before the row renders it.
  EngineConnectProgressPayloadSchema,
} from './event-payloads.schema.js';

// Schema map — derived from the event registry (single authored wire contract).
// Re-exported here so `@bash/shared/schemas` keeps surfacing EventSchemas.
export { EventSchemas, type EventSchemaKey } from '../events/registry.js';

// Task payload schemas
export {
  TaskOutputTypeSchema,
  TaskOutputSchema,
  TaskTodoGroupSchema,
  SerializedTaskSchema,
  TaskSchema,
  TaskCreatedPayloadSchema,
  TaskResumePayloadSchema,
  TaskResumedPayloadSchema,
  TaskListPayloadSchema,
  TaskUpdatePayloadSchema,
  TaskDestroyPayloadSchema,
  TaskTitleEditPayloadSchema,
  TaskResumeFailedPayloadSchema,
  TaskStatsPayloadSchema,
  TaskStreamPayloadSchema,
  TaskOutputRegisteredPayloadSchema,
  TaskArtifactRegisteredPayloadSchema,
  TaskPreviewReadyPayloadSchema,
  TaskPreviewClosedPayloadSchema,
  TaskAppSchema,
  AppsChangedPayloadSchema,
  AppsViewPayloadSchema,
} from './task-payloads.schema.js';

// Credit payload schemas
export {
  CreditUpdatePayloadSchema,
  CreditWarningPayloadSchema,
  CreditExhaustedPayloadSchema,
  CreditSyncPayloadSchema,
  CreditToolCheckPayloadSchema,
  CreditToolVerdictPayloadSchema,
  CreditApprovalNeededPayloadSchema,
} from './credit-payloads.schema.js';

// Staging & usage payload schemas
export {
  AgentUsagePayloadSchema,
  ContextUsageSnapshotSchema,
  AgentContextUsagePayloadSchema,
  MemoryRecallEntrySchema,
  AgentMemoryRecallPayloadSchema,
  MessageStagedPayloadSchema,
  MessagePickedUpPayloadSchema,
  CancelStagedPayloadSchema,
  StagingSyncRequestPayloadSchema,
  StagingSyncPayloadSchema,
  StagedEnvelopeSchema,
  CheckpointContinuePayloadSchema,
} from './staging-payloads.schema.js';

// Connection payload schemas
// Phase 9: ConnectionResolvedPayloadSchema removed — OAuth completion now
// flows through INTERACTION_RESPONSE for kind:'composio_auth'.
export {
  ConnectionRequiredPayloadSchema,
} from './connection-payloads.schema.js';

// Turn outcome schema (termination taxonomy)
export { TurnOutcomeSchema } from './turn-outcome.schema.js';

// Agent trace store schemas (observability Slice B)
export {
  ArmSchema,
  SurfaceSchema,
  TerminalStatusSchema,
  ErrorSourceSchema,
  ErrorSeveritySchema,
  AgentTurnRecordSchema,
  ToolCallRecordSchema,
  ErrorEventRecordSchema,
} from './agent-trace.schema.js';

// Environment schemas
export {
  ServerEnvSchema,
  AgentEnvSchema,
  FrontendEnvSchema,
  parseServerEnv,
  parseAgentEnv,
  parseFrontendEnv,
  safeParseServerEnv,
  safeParseAgentEnv,
  type ServerEnv,
  type AgentEnv,
  type FrontendEnv,
} from './env.schema.js';

// ContentBlock schemas
export {
  // Enums
  ToolStatusSchema,
  ToolIconHintSchema,
  CreditApprovalStatusSchema,
  RoutineTriggerTypeSchema,

  // Nested objects
  ToolSummarySchema,

  // Individual block schemas
  TextBlockSchema,
  ImageBlockSchema,
  ThinkingBlockSchema,
  ToolUseBlockSchema,
  ToolResultBlockSchema,
  QuestionBlockSchema,
  TodoBlockSchema,
  ErrorBlockSchema,
  ErrorCategorySchema,
  FileAttachmentBlockSchema,
  MediaBlockSchema,
  CreditApprovalBlockSchema,
  RoutineConfirmationBlockSchema,
  ItemConfirmationBlockSchema,
  TurnFooterBlockSchema,
  NoticeBlockSchema,
  NOTICE_TEXT_MAX,

  // Discriminated union
  ContentBlockSchema,
} from './content-blocks.schema.js';

// Interaction — unified user-input schemas
export {
  InteractionKindSchema,
  PermissionSuggestionSchema,
  AskQuestionRequestSchema,
  ComposioAuthRequestSchema,
  ToolPermissionRequestSchema,
  ToolPermissionDetailSchema,
  ToolPermissionReasonSchema,
  CreditApprovalRequestSchema,
  ExitPlanRequestSchema,
  CustomInteractionRequestSchema,
  InteractionRequestSchema,
  InteractionDenyReasonSchema,
  InteractionAllowSchema,
  InteractionDenySchema,
  InteractionOutcomeSchema,
  InteractionResponseSchema,
  InteractionRequiredPayloadSchema,
  InteractionRequiredEnvelopeSchema,
  InteractionResponsePayloadSchema,
} from './interaction.schema.js';

// Lane payloads — the sandbox→browser tool lifecycle + subagent lane events (s2-07d)
export {
  AgentToolUseStartPayloadSchema,
  AgentToolProgressPayloadSchema,
  AgentStatusPayloadSchema,
  ENGINE_SIGN_IN_STATUS,
  SubagentStartPayloadSchema,
  SubagentTextDeltaPayloadSchema,
  SubagentToolUsePayloadSchema,
  SubagentToolResultPayloadSchema,
  SubagentEndPayloadSchema,
} from './lane-payloads.schema.js';
export type {
  AgentToolUseStartPayload,
  AgentToolProgressPayload,
  AgentStatusPayload,
  SubagentStartPayload,
  SubagentTextDeltaPayload,
  SubagentToolUsePayload,
  SubagentToolResultPayload,
  SubagentEndPayload,
} from './lane-payloads.schema.js';

// Sync payloads — message:sync / message:gap_warning / user_message:broadcast (s2-07d)
export {
  MessageSyncMessageSchema,
  MessageSyncPayloadSchema,
  MessageGapWarningPayloadSchema,
  UserMessageBroadcastPayloadSchema,
} from './sync-payloads.schema.js';
export type {
  MessageSyncMessage,
  MessageSyncWirePayload,
  MessageGapWarningPayload,
  UserMessageBroadcastPayload,
} from './sync-payloads.schema.js';

// GTM Lead Engine — admin-gtm request schemas
export {
  GtmLeadStageSchema,
  GtmTierSchema,
  GtmChannelSchema,
  GtmProspectQuerySchema,
  GtmImportRequestSchema,
  GtmManualRowSchema,
  GtmManualImportRequestSchema,
  GtmSignalTypeSchema,
  GtmSignalPlatformSchema,
  GtmEngagementRowSchema,
  GtmEngagementImportRequestSchema,
  GtmLeadListQuerySchema,
  GtmDraftRequestSchema,
  GtmBulkDraftRequestSchema,
  GtmSendRequestSchema,
  GtmLeadExportRequestSchema,
  GtmBulkEnrichRequestSchema,
  GtmWaterfallRequestSchema,
  GtmLeadPatchSchema,
  GtmSequenceStepSchema,
  GtmSequenceUpsertSchema,
  GtmEnrollRequestSchema,
  GtmEnrollmentActionSchema,
  GtmLaunchUpsertSchema,
  GtmDirectoryPatchSchema,
  GtmWeeklyToggleSchema,
  GtmContentPlatformSchema,
  GtmSnipeSubmitSchema,
  GtmSnipePatchSchema,
  GtmInfluencerPatchSchema,
  GtmLeadMagnetUpsertSchema,
  GtmDesignPartnerUpsertSchema,
  GtmDesignPartnerPatchSchema,
  GtmPmfSnapshotUpsertSchema,
  GtmDiscoveryCategorySchema,
  GtmDiscoveryPricingModelSchema,
  GtmDiscoveryPlatformCreateSchema,
  GtmDiscoveryPlatformPatchSchema,
  GtmListingProfileUpsertSchema,
  GtmDiscoveryResearchRequestSchema,
} from './gtm.schema.js';
export type {
  GtmProspectQuery,
  GtmImportRequest,
  GtmManualRow,
  GtmManualImportRequest,
  GtmEngagementRow,
  GtmEngagementImportRequest,
  GtmLeadListQuery,
  GtmDraftRequest,
  GtmBulkDraftRequest,
  GtmSendRequest,
  GtmLeadExportRequest,
  GtmBulkEnrichRequest,
  GtmWaterfallRequest,
  GtmSequenceUpsert,
  GtmEnrollRequest,
  GtmEnrollmentAction,
  GtmLaunchUpsert,
  GtmDirectoryPatch,
  GtmWeeklyToggle,
  GtmSnipeSubmit,
  GtmSnipePatch,
  GtmInfluencerPatch,
  GtmLeadMagnetUpsert,
  GtmLeadPatch,
  GtmDiscoveryPlatformCreate,
  GtmDiscoveryPlatformPatch,
  GtmListingProfileUpsert,
  GtmDiscoveryResearchRequest,
} from './gtm.schema.js';

// Admin-panel access — admin_roles grant/revoke request schemas
export {
  AdminAccessRoleSchema,
  AdminRoleGrantSchema,
} from './admin-roles.schema.js';
export type {
  AdminAccessRole,
  AdminRoleGrantInput,
  AdminRoleRow,
} from './admin-roles.schema.js';

// Work Health — admin-work-health request schemas
export {
  CreateWorkHealthMemberSchema,
  UpdateWorkHealthMemberSchema,
  CreateWorkHealthResponsibilitySchema,
  UpdateWorkHealthResponsibilitySchema,
  CreateWorkHealthUsageSchema,
} from './work-health.schema.js';
export type {
  CreateWorkHealthMemberInput,
  UpdateWorkHealthMemberInput,
  CreateWorkHealthResponsibilityInput,
  UpdateWorkHealthResponsibilityInput,
  CreateWorkHealthUsageInput,
} from './work-health.schema.js';

// Injection screen — the runner→server fault beacon's wire contract. Shared
// because BOTH halves must agree byte for byte: the seam builds the body, the
// route validates it, and a drift 400s every report into an undrainable backlog.
export {
  InjectionScreenFaultSchema,
  InjectionScreenFaultBatchSchema,
  InjectionScreenAnswerSchema,
  INJECTION_SCREEN_FAULT_BATCH_MAX,
} from './injection-screen.schema.js';
export type {
  InjectionScreenFault,
  InjectionScreenFaultBatch,
  InjectionScreenAnswer,
} from './injection-screen.schema.js';

// NOTE: z re-export removed — import zod directly where needed

export * from './runner-status.schema.js';
export * from './trace-spans.schema.js';
// The engine's stream-stall anomaly (sandbox→server, never forwarded).
export * from './stream-stalled.schema.js';
// Task history over the relay (harness to nine R3: fork).
export * from './task-history.schema.js';
// B5c (harness to nine P1): the forwarded events that had no schema, written from their producers.
export * from './forwarded-payloads.schema.js';
// B5c item 3: the runner protocol's busiest frames, written from their producers.
export * from './runner-protocol.schema.js';
// B5c item 3, second batch: the client protocol (presence, credits, browser and terminal controls, jobs, prewarm, Nami).
export * from './client-protocol.schema.js';
// B5c item 3, third batch: the support chat and the admin dashboard.
export * from './support-admin-protocol.schema.js';
// B5c item 3, fourth batch: the os relay RPCs between the server and the local runner.
export * from './os-protocol.schema.js';
// Browser fetch (POST /v1/users/{user_id}/browser/fetch): the server ↔ runner pair, and the one domain rule.
export * from './browser-fetch.schema.js';
// The box door (box hosts spec §6): server ↔ runner file, exec and logs requests for a host that dials out.
export * from './box-door.schema.js';
// Browser watch (GET /v1/users/{user_id}/browser/watch): the server ↔ runner lease and the runner's tab events.
export * from './browser-watch.schema.js';
